import { toast } from 'react-toastify';
import moment from 'moment';
import hmacSHA256 from 'crypto-js/hmac-sha256';
import { Country, State } from 'country-state-city';
import { isMobile } from 'react-device-detect';

import config from '../config/config';
import NotificationCard from '../shared/components/NotificationCard';
import { formatRelative } from 'date-fns';
import { DefaultDisplayValue } from 'app/enums';

export const apiToastSuccess = (data) => {
  if (data && data.data && data.data.responseMessage) {
    toast.dark(data.data.responseMessage, {
      position: 'top-right',
      className: 'dark-success',
    });
  }
};

export const apiToastError = (error) => {
  toast.dark(getErrorMessage(error), { position: 'top-right', className: 'dark-error' });
};

export const getErrorMessage = (error) => {
  const errorMessages = Array.isArray(error?.response?.data?.message)
    ? error?.response?.data?.message.join('\n')
    : error?.response?.data?.message;

  return error?.response?.data?.responseMessage || errorMessages || error?.message;
};

export const toastLoading = () => {
  const toastId = toast.dark(
    <div className="lds-ring">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>,
    {
      toastId: 'loading',
      position: 'bottom-left',
      className: 'dark-success',
      closeButton: false,
      autoClose: true,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      style: {
        backgroundColor: 'transparent',
        border: 'none',
        width: '64px',
      },
    }
  );
  const dismiss = () => toast.dismiss(toastId);
  return dismiss;
};

export const toastSuccess = (message, options = {}) => {
  return toast.dark(message, {
    position: 'top-right',
    className: 'dark-success',
    ...options,
  });
};

export const toastError = (message, options = {}) => {
  return toast.dark(message, {
    position: 'top-right',
    className: 'dark-error',
    ...options,
  });
};

export const toastNotification = (data = null) => {
  toast.dark(<NotificationCard data={data} />, {
    position: 'bottom-right',
    className: 'dark-success',
    hideProgressBar: true,
    autoClose: true,
  });
};

export const getApiEndpoint = () => {
  return config.apiEndPoint.replace(/[^\x20-\x7E]/g, '');
};

export const getApiResponseCode = (data, defaultReturn = null) => {
  if (data && data.responseCode) {
    return data.responseCode;
  }

  return defaultReturn;
};

export const getApiResponseMessage = (data, defaultReturn = null) => {
  if (data && data.responseMessage) {
    return data.responseMessage;
  }

  return defaultReturn;
};

export const getApiSingleDetails = (data, defaultReturn = null) => {
  if (data && data.responseData && Object.keys(data.responseData).length > 0) {
    return data.responseData;
  }

  return defaultReturn;
};

export const getApiArrayDetails = (data, defaultReturn = null) => {
  if (
    data &&
    data.responseData &&
    Array.isArray(data.responseData) &&
    data.responseData.length > 0
  ) {
    return data.responseData;
  }

  return defaultReturn;
};

export const isValidArray = (data) => {
  return data && Array.isArray(data) && data.length > 0;
};

export const isValidObject = (data) => {
  return typeof data === 'object' && data ? Object.keys(data).length > 0 : false;
};

export const setLocalStorage = (key, value) => {
  localStorage.setItem(key, value);
};

export const getLocalStorage = (key) => {
  return localStorage.getItem(key);
};

export const removeLocalStorage = (key) => {
  localStorage.removeItem(key);
};

export const clearLocalStorage = () => {
  localStorage.clear();
};

export const isActiveRoute = (currentRoute, routeName) => {
  return currentRoute === routeName;
};

export const getNonce = () => {
  return Math.round(new Date().getTime() / 1000);
};

export const getHeaderTimestamp = () => {
  return Math.round(new Date().getTime() / 1000) * 1000;
};

export const generateHeaderToken = () => {
  const nonce = getNonce();
  const timestamp = getHeaderTimestamp();

  const secretKey = '';
  const privateKey = '';

  const tokenString = 'nonce=' + nonce + '&timestamp=' + timestamp + '|' + secretKey;

  return hmacSHA256(tokenString, privateKey).toString();
};

export const getPastYears = () => {
  const max = new Date().getFullYear();
  const min = max - 100;
  let years = [];

  for (let i = max; i >= min; i--) {
    years.push(i);
  }
  return years;
};

export const getMonths = () => {
  const monthsArr = moment.monthsShort();
  return monthsArr.map((data, i) => ({
    name: data,
    index: i + 1,
  }));
};

export const getDaysArray = (year, month) => {
  const monthIndex = (month || moment()?.month()) - 1; // 0..11 instead of 1..12
  const date = new Date(year || moment()?.year(), monthIndex, 1);
  const days = [];
  while (date.getMonth() === monthIndex) {
    days.push(date.getDate());
    date.setDate(date.getDate() + 1);
  }
  return days;
};

const countryList = Country.getAllCountries();

export const getCountryList = () => {
  return countryList.map((country) => {
    return { value: country?.isoCode, label: country?.name };
  });
};

export const getCountryNameByCode = (code) => {
  return Country.getCountryByCode(code)?.name ?? DefaultDisplayValue.NA;
};

export const getQueryParam = (location, param) => {
  const query = new URLSearchParams(location.search);
  return query.get(param);
};

export const getTeamOptions = (min, max) => {
  let teamOptions = [];
  for (let range = min; range <= max; range++) {
    teamOptions = [...teamOptions, { label: range, value: range }];
  }
  return teamOptions;
};
export const userPermission = (userRole, permission = '') => userRole?.includes(permission);

export const manualWeightObject = (manualRanksPercentage, type = '', maxTeam = '') => {
  let value = manualRanksPercentage;
  let manualWeightObjects = [];
  if (type === 'top-percent') {
    value = (maxTeam * manualRanksPercentage) / 100;
  }
  for (let range = 1; range <= Math.ceil(value); range++) {
    manualWeightObjects = [
      ...manualWeightObjects,
      { poolWeight: range === 1 ? 1 : '', matchRanking: range },
    ];
  }
  return manualWeightObjects;
};

export const getProcessingFee = (amount = 0, type = '') => {
  if (amount) {
    if (type === 'withdraw') {
      const mininum = 1.25;
      const fees = (3.5 * amount) / 100;
      return fees < mininum ? mininum : fees;
    }
    const selectedAmount = parseFloat(amount);
    return (selectedAmount + 0.3) / (1 - 0.044) - selectedAmount;
  }

  return 0;
};

export const getTimeZone = (time) => {
  if (time) {
    const now = new Date();
    return formatRelative(new Date(time), now);
  } else {
    return 'FIXME';
  }
};

export const formatMoney = (amount, currency) => {
  return (parseFloat(amount) / 100).toLocaleString(
    undefined, // leave undefined to use the visitor's browser
    // locale or a string like 'en-US' to override it.
    { style: 'currency', currency }
  );
};

export const isMobileOrTablet = () => {
  return isMobile;
};

export const handleEnter = (e, formElement = null, field = null) => {
  try {
    if (e.keyCode === 13) {
      const form = e.target.form;
      const index = Array.prototype.indexOf.call(form, e.target);
      if (formElement === 'login' && field === 'password') {
        form?.elements[index + 2]?.click();
      } else {
        form?.elements[index + 1]?.focus();
      }
      e.preventDefault();
    }
  } catch (error) {
    console.error('handleEnter ~ error', error);
  }
};
